import React, { useRef, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FormControl,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Icon,
  Popper,
  Paper,
  Typography,
  Box,
  ClickAwayListener,
  CircularProgress,
} from "@mui/material";
import { FixedSizeList as List } from "react-window";
import SearchIcon from "@mui/icons-material/Search";
import {
  getCategoryValues,
  getSearchResult,
} from "app/main/apps/settings/store/configurationsSlice";
import {
  formatDateString,
  stageNametoURLNameMapping,
} from "app/main/apps/common/AuraFunctions";

function GlobalSearch() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [moduleList, setModuleList] = useState([]);
  const [selectedModule, setSelectedModule] = useState("");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const anchorRef = useRef(null);

  /**
   * Initialize Data
   */
  useEffect(() => {
    if (!moduleList || moduleList?.length <= 0) {
      dispatch(
        getCategoryValues({
          params: { c: "search_module" },
        })
      )
        .unwrap()
        .then((response) => {
          const moduleListTemp = response && response.data ? response.data : [];
          setModuleList(moduleListTemp);
        });
    }
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpenPopup(false);
        setResults([]);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const closePopup = () => {
    setOpenPopup(false);  // Close the popup
    setResults([]); // Clear the results
  }

  /**
   * Handle search
   */
  const handleSearch = () => {
    setIsLoading(true);
    dispatch(
      getSearchResult({ params: { module_name: selectedModule, q: search } })
    )
      .then((res) => {
        if (!res?.error) {
          const resData = res?.payload?.orders || [];
          setResults(resData);
        } else {
          setResults([]);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        setResults([]);
      });
    setOpenPopup(true);
  };

  /**
   * Navigate on order item click
   */
  const handleOrderItemClick = (item) => {
    navigate(
      `/${stageNametoURLNameMapping[item?.stage]}?orderNo=${item?.orderNo}`,
      { replace: true }
    );
    closePopup();
  };

  /**
   * Navigate on invoice item click
   */
  const handleInvoiceItemClick = (item) => {
    const basePath = item.isCustomer ? "/invoices" : "/supplier-invoices";
    navigate(`${basePath}?invoice_no=${item?.invoiceNumber}`, { replace: true });
    closePopup();
  };

  /**
   * Search List Invoice Item
   */
  const InvoiceResultRow = ({ index, style, data, item }) => {
    return (
      <Box
        style={style}
        onClick={() => {
          handleInvoiceItemClick(item, );
        }}
        className="flex items-center justify-center justify-between border-b py-2 px-12 last:border-b-0 cursor-pointer  hover:bg-gray-100 transition duration-150"
      >
        <div>
          <div className="font-semibold">
            {item?.invoiceNumber?.toString().trim()}
          </div>
          <div className="capitalize text-grey-700">Module - {item?.isCustomer ? "Invoice" : "Supplier Invoice"}</div>
        </div>
        {item?.date && (
          <div className="text-grey-700">
            Created on {formatDateString(item.date)}
          </div>
        )}
      </Box>
    );
  };


  /**
   * Search List Item
   */
  const ResultRow = ({ index, style, data }) => {
    const item = data?.[index];
    if (selectedModule?.toLowerCase() === "order") {
      return (
        <Box
          style={style}
          onClick={() => {
            handleOrderItemClick(item);
          }}
          className="flex items-center justify-center justify-between border-b py-2 px-12 last:border-b-0 cursor-pointer  hover:bg-gray-100 transition duration-150"
        >
          <div>
            <div className="font-semibold">
              {item?.orderNo?.toString().trim()}
            </div>
            <div className="capitalize text-grey-700">
              Module - {item?.stage}
            </div>
          </div>
          {item?.date && (
            <div className="text-grey-700">
              Created on {formatDateString(item.date)}
            </div>
          )}
        </Box>
      );
    }
    if (selectedModule?.toLowerCase() === "invoice") {
      return <InvoiceResultRow index={index} style={style} data={data} item={item} />
    }
    // Update here for other module result
    return (
      <Box
        style={style}
        className="flex items-center justify-center justify-between border-b py-2 px-12 last:border-b-0"
      >
        <div className="text-grey-700">
          Created on {formatDateString(item?.date)}
        </div>
      </Box>
    );
  };

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
          return; // Don't close if click is inside the search bar
        }
        closePopup();
      }}
    >
      <div className="flex items-center justify-center relative">
        <div
          ref={anchorRef}
          className="flex border shadow-sm rounded-md overflow-hidden w-[40rem] md:w-[50rem] bg-white h-[35px]"
        >
          <FormControl
            variant="standard"
            className="min-w-[15rem] md:min-w-[20rem] px-2"
            sx={{ height: "100%", borderRight: "1px solid #EEE" }}
          >
            <Select
              value={selectedModule ?? ''}
              onChange={(e) => setSelectedModule(e.target.value)}
              displayEmpty
              MenuProps={{ PaperProps: { className: "mt-1" } }}
              sx={{
                height: "35px",
                minHeight: "35px",
                maxHeight: "35px",
                backgroundColor: "transparent !important",
                '&:before, &:after': { display: 'none' },
                "&:focus": {
                  backgroundColor: "transparent !important",
                },
                "& .MuiInputBase-input": {
                  backgroundColor: "transparent !important",
                },
                "&.Mui-focused": {
                  backgroundColor: "transparent !important",
                },
                "&.MuiInputBase-root": {
                  backgroundColor: "transparent !important",
                },
                "& .MuiSelect-select": {
                  height: "35px",
                  minHeight: "35px",
                  maxHeight: "35px",
                  display: "flex",
                  alignItems: "center",
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingLeft: "0px",
                  paddingRight: "32px", // leave space for dropdown icon
                },
                "& .MuiSelect-icon": {
                  top: "50%",
                  transform: "translateY(-50%)",
                },
              }}
            >
              <MenuItem value="">
                <span className="text-gray-500">Select Module</span>
              </MenuItem>
              {moduleList?.map((mod) => (
                <MenuItem key={mod.name} value={mod.name}>
                  <div className="flex items-center gap-8">
                    <Icon fontSize="small" className="text-grey">
                      {mod.reference}
                    </Icon>
                    <span>{mod.name}</span>
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                search.trim() !== "" &&
                selectedModule.trim() !== ""
              ) {
                handleSearch();
              }
            }}
            variant="standard"
            placeholder="Enter Search here"
            fullWidth
            slotProps={{
              input: {
                className: "px-3 h-full",
                sx: {
                  height: "35px",
                  minHeight: "35px",
                  maxHeight: "35px",
                  display: "flex",
                  alignItems: "center",
                  '&:before, &:after': { display: 'none' },
                },
              },
            }}
            sx={{
              height: "35px",
              minHeight: "35px",
              maxHeight: "35px",
              backgroundColor: "white",
            }}
            className="bg-white h-full"
          />

          <IconButton
            disabled={!search || !selectedModule}
            onClick={handleSearch}
            sx={{
              height: "100%",
              px: 1,
              borderRadius: 0,
              backgroundColor: "secondary.main",
              color: "white",
              "&:hover": {
                backgroundColor: "secondary.dark",
              },
            }}
          >
            <SearchIcon fontSize="small" />
          </IconButton>
        </div>

        {/* Result Popper */}
        <Popper
          open={openPopup}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
        >
          <Paper className="border-1 shadow-lg max-h-[400px] mt-4 w-full rounded-md">
            {isLoading && (
              <div className="flex items-center justify-center h-[400px]">
                <CircularProgress />
              </div>
            )}
            {!isLoading && results.length === 0 && (
              <div className="flex items-center justify-center h-[400px]">
                <Typography className="text-18 text-red">
                  No results found.
                </Typography>
              </div>
            )}
            {!isLoading && Array.isArray(results) && results.length > 0 && (
              <List
                height={400}
                itemCount={results.length}
                itemSize={50}
                width="100%"
                itemKey={(index) => {
                  const item = results[index];
                  return `${item?.orderNo}-${index}`;
                }}
                itemData={results} // <-- This is what passes `data` to Row
              >
                {ResultRow}
              </List>
            )}
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
}

export default GlobalSearch;
